import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import MapView from "@/components/shared/MapView";
import EmptyState from "@/components/shared/EmptyState";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flame, MapPin, Clock, Radio, Loader2, Send } from "lucide-react";
import { formatDisplayId } from "@/lib/utils";
import { toast } from "sonner";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import LocationLabel from "@/components/shared/LocationLabel";

const TERMINAL_SERVICE_RESPONSE_STATUSES = ["RESOLVED", "UNABLE_TO_RESPOND"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeSeverity(value) {
  const candidate = String(value || "low").toLowerCase().trim();
  return ["critical", "high", "medium", "low"].includes(candidate) ? candidate : "low";
}

function formatType(value) {
  return String(value || "Incident")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function parseCoords(raw) {
  if (!raw) return null;
  if (Array.isArray(raw) && raw.length >= 2) {
    const lat = Number.parseFloat(raw[0]);
    const lon = Number.parseFloat(raw[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon];
  }
  const lat = Number.parseFloat(raw?.lat ?? raw?.latitude);
  const lon = Number.parseFloat(raw?.lon ?? raw?.lng ?? raw?.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon];
  return null;
}

function getPrimaryVehicle(incident) {
  const vehicles = Array.isArray(incident?.vehicles) ? incident.vehicles : [];
  const primary = vehicles.find((v) => String(v?.role || "").toUpperCase() === "PRIMARY") || vehicles[0];
  return {
    vehicleId: primary?.vehicle_id || primary?.id || null,
    plate: primary?.registration_number || primary?.plate || "—",
  };
}

function mapIncident(item) {
  const { vehicleId, plate } = getPrimaryVehicle(item);
  return {
    id: item?.id || item?.incident_id || "",
    status: String(item?.status || "RESPONDING").toUpperCase(),
    type: formatType(item?.incident_type || item?.type),
    severity: normalizeSeverity(item?.severity),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    location:
      item?.location?.address ||
      item?.location ||
      item?.address ||
      item?.place ||
      [item?.city, item?.state].filter(Boolean).join(", ") ||
      "Location unavailable",
    coords: parseCoords(item?.location) || parseCoords(item),
    eventTime: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
    description: item?.description || "",
    vehicleId,
    vehiclePlate: plate,
    raw: item,
  };
}

function mapServiceLog(entry) {
  return {
    id: entry?.id || `${entry?.org_type || "ORG"}-${entry?.updated_at || entry?.created_at || Math.random()}`,
    orgType: String(entry?.org_type || "").toUpperCase(),
    status: String(entry?.status || "").toUpperCase(),
    note: entry?.note || "",
    updatedAt: entry?.updated_at || entry?.created_at || "",
  };
}

function mapTelemetry(value) {
  const coords = parseCoords(value?.location || value);
  const speed = Number.parseFloat(value?.gps_speed ?? value?.speed ?? value?.speed_kmh);
  return {
    coords,
    speed: Number.isFinite(speed) ? speed : null,
    event: value?.event || value?.status || "",
    timestamp: value?.timestamp || value?.last_updated || value?.created_at || "",
  };
}

export default function FireActiveIncidents() {
  const { can } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });

  const [selectedId, setSelectedId] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseStatus, setResponseStatus] = useState(TERMINAL_SERVICE_RESPONSE_STATUSES[0]);
  const [responseNote, setResponseNote] = useState("");
  const [responseSubmitting, setResponseSubmitting] = useState(false);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [serviceLogsLoading, setServiceLogsLoading] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/incidents", {
        params: { status: "RESPONDING", page, page_size: pageSize },
      });

      const rows = normalizeCollection(data).map(mapIncident).filter((item) => item.id);
      setList(rows);
      setMeta(data?.meta || { total: rows.length, page, page_size: pageSize, pages: 1 });

      setSelectedId((prev) => {
        if (rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id || null;
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load active incidents.");
      setList([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const selected = useMemo(() => list.find((i) => i.id === selectedId) || null, [list, selectedId]);

  const fetchServiceLogs = useCallback(async (incidentId) => {
    if (!incidentId) {
      setServiceLogs([]);
      return;
    }
    setServiceLogsLoading(true);
    try {
      const { data } = await api.get(`/incidents/${incidentId}/service-responses`, {
        params: { page: 1, page_size: 100 },
      });
      const rows = normalizeCollection(data).map(mapServiceLog);
      setServiceLogs(rows);
    } catch (err) {
      toast.error(extractApiError(err).message || "Failed to load service response log.");
      setServiceLogs([]);
    } finally {
      setServiceLogsLoading(false);
    }
  }, []);

  const fetchTelemetry = useCallback(async (vehicleId) => {
    if (!vehicleId) {
      setTelemetry(null);
      return;
    }
    setTelemetryLoading(true);
    try {
      const response = await api.get(`/vehicles/${vehicleId}/telemetry/latest`);
      setTelemetry(mapTelemetry(unwrapSingle(response) ?? undefined));
    } catch (err) {
      toast.error(extractApiError(err).message || "Failed to load vehicle telemetry.");
      setTelemetry(null);
    } finally {
      setTelemetryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selected) {
      setServiceLogs([]);
      setTelemetry(null);
      return;
    }
    fetchServiceLogs(selected.id);
    fetchTelemetry(selected.vehicleId);
  }, [selected, fetchServiceLogs, fetchTelemetry]);

  const openTerminalResponseDialog = () => {
    if (!canUpdateIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    setResponseStatus(TERMINAL_SERVICE_RESPONSE_STATUSES[0]);
    setResponseNote("");
    setResponseDialogOpen(true);
  };

  const submitTerminalResponse = async () => {
    if (!canUpdateIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    if (!selected?.id) return;

    setResponseSubmitting(true);
    try {
      const payload = {
        status: responseStatus,
        ...(responseNote.trim() ? { note: responseNote.trim() } : {}),
      };
      await api.post(`/incidents/${selected.id}/service-response`, payload);
      toast.success(`Service response logged: ${responseStatus}.`);
      setResponseDialogOpen(false);
      await fetchIncidents();
      await fetchServiceLogs(selected.id);
    } catch (err) {
      toast.error(extractApiError(err).message || "Failed to log service response.");
    } finally {
      setResponseSubmitting(false);
    }
  };

  const mapMarkers = useMemo(() => {
    if (!selected) return [];
    const marks = [];
    if (telemetry?.coords) {
      marks.push({
        coords: telemetry.coords,
        title: selected.vehiclePlate,
        subtitle: telemetry.speed !== null ? `Speed ${telemetry.speed.toFixed(1)} km/h` : "Live vehicle",
        severity: "low",
        type: "vehicle",
      });
    }
    if (selected.coords) {
      marks.push({
        coords: selected.coords,
        title: selected.id,
        subtitle: selected.location,
        severity: selected.severity,
        type: "incident",
      });
    }
    return marks;
  }, [selected, telemetry]);

  const center = useMemo(() => telemetry?.coords || selected?.coords || [19.076, 72.8777], [selected, telemetry]);

  return (
    <div className="space-y-6" data-testid="fire-active">
      <PageHeader
        title="Active Incidents"
        subtitle="Fire response in progress (RESPONDING)."
        actions={<Button variant="outline" onClick={fetchIncidents} disabled={loading}>{loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Refresh</Button>}
      />

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-3">
          {loading && list.length === 0 ? (
            <SectionCard>
              <div className="flex items-center justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
            </SectionCard>
          ) : null}

          {!loading && list.length === 0 ? (
            <EmptyState title="No active incidents" description="No incidents are currently in RESPONDING state." icon={Flame} />
          ) : null}

          {list.map((incident) => (
            <button
              key={incident.id}
              onClick={() => setSelectedId(incident.id)}
              className={`w-full rounded-xl border bg-white p-4 text-left transition ${selectedId === incident.id ? "border-[hsl(var(--role-primary))] shadow-md" : "border-slate-100 hover:border-slate-200"}`}
            >
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">{formatDisplayId(incident.id)}</p>
                <SeverityBadge severity={incident.severity} />
              </div>
              <p className="mt-1 font-semibold text-slate-900">{incident.type}</p>
              <LocationLabel locationValue={incident.locationRaw ?? incident.location} className="mt-0.5 text-xs text-slate-500" />
              <div className="mt-2 flex items-center justify-between">
                <StatusBadge status={incident.status.toLowerCase()} />
                <span className="text-xs text-slate-500">{formatTime(incident.eventTime)}</span>
              </div>
            </button>
          ))}

          <SectionCard>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Page {meta.page || page} of {meta.pages || 1}</span>
              <div className="flex items-center gap-2">
                <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                  <SelectTrigger className="h-9 w-[100px]"><SelectValue placeholder="Size" /></SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50].map((size) => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <Button size="sm" variant="outline" disabled={loading || page >= (meta.pages || 1)} onClick={() => setPage((p) => Math.min(meta.pages || 1, p + 1))}>Next</Button>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4 xl:col-span-2">
          {selected ? (
            <>
              <SectionCard padded={false}>
                <div className="p-3">
                  <MapView markers={mapMarkers} center={center} zoom={14} height={360} testId="fire-active-map" />
                </div>
              </SectionCard>

              <SectionCard title={`${selected.type} · ${formatDisplayId(selected.id)}`} subtitle={<LocationLabel locationValue={selected.locationRaw ?? selected.location} />}>
                <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">Vehicle</p><p className="mt-0.5 font-semibold text-slate-900">{selected.vehiclePlate}</p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">Status</p><p className="mt-0.5"><StatusBadge status={selected.status.toLowerCase()} /></p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">Last update</p><p className="mt-0.5 font-semibold text-slate-900">{formatTime(selected.eventTime)}</p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">Telemetry</p><p className="mt-0.5 font-semibold text-slate-900">{telemetryLoading ? "Loading..." : telemetry?.speed !== null ? `${telemetry?.speed?.toFixed(1)} km/h` : "—"}</p></div>
                </div>

                {selected.description ? ( 
                  <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    {selected.description}
                  </div>
                ) : null}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {canUpdateIncidents ? (
                    <Button onClick={openTerminalResponseDialog} disabled={responseSubmitting} className="gap-2">
                      {responseSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Log Terminal Response
                    </Button>
                  ) : null}
                  <Button variant="outline" onClick={() => fetchTelemetry(selected.vehicleId)} disabled={telemetryLoading}>
                    <Radio size={14} className="mr-1" /> Refresh telemetry
                  </Button>
                </div>
              </SectionCard>

              <SectionCard title="Service response log" subtitle="GET /incidents/{id}/service-responses">
                {serviceLogsLoading ? (
                  <div className="flex items-center text-sm text-slate-500"><Loader2 size={14} className="mr-2 animate-spin" /> Loading log...</div>
                ) : serviceLogs.length === 0 ? (
                  <p className="text-sm text-slate-500">No service response entries yet.</p>
                ) : (
                  <div className="space-y-2">
                    {serviceLogs.map((entry) => (
                      <div key={entry.id} className="rounded-xl border border-slate-100 bg-white px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-slate-600">{entry.orgType || "SERVICE"}</span>
                            <StatusBadge status={entry.status.toLowerCase()} />
                          </div>
                          <span className="text-xs text-slate-500">{formatTime(entry.updatedAt)}</span>
                        </div>
                        {entry.note ? <p className="mt-2 text-sm text-slate-700">{entry.note}</p> : null}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </>
          ) : (
            <SectionCard>
              <div className="flex items-center gap-2 text-sm text-slate-500"><MapPin size={14} /> Select an incident to view details.</div>
            </SectionCard>
          )}
        </div>
      </div>
      <Dialog open={responseDialogOpen} onOpenChange={(open) => !responseSubmitting && setResponseDialogOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Terminal Service Response</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={responseStatus} onValueChange={setResponseStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TERMINAL_SERVICE_RESPONSE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note (Optional)</Label>
              <Textarea
                value={responseNote}
                onChange={(event) => setResponseNote(event.target.value)}
                placeholder="e.g. Fire suppression complete and scene secured"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseDialogOpen(false)} disabled={responseSubmitting}>Cancel</Button>
            <Button onClick={submitTerminalResponse} disabled={responseSubmitting} className="gap-2">
              {responseSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Log update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
